import { useState } from 'react'
import {
  issueEquipment,
  loadEquipment,
  returnEquipment,
  type EquipmentCondition,
  type EquipmentItem,
} from '@/data/portal'
import { useAsync } from '@/data/useAsync'
import { Failed, Loading } from '@/components/bits'
import { shortDate, today } from '@/lib/format'
import type { PersonDetail } from '@/data/queries'
import { useOrg } from '@/org/OrgProvider'

const CATEGORIES = ['Keys and fobs', 'Uniform', 'Radio', 'Phone', 'Laptop', 'Tools', 'Locker', 'Other']

const CONDITIONS: readonly (readonly [EquipmentCondition, string])[] = [
  ['as_new', 'As new'],
  ['good', 'Good'],
  ['fair', 'Fair'],
  ['damaged', 'Damaged'],
  ['lost', 'Lost'],
  ['not_returned', 'Not returned'],
]

type State = 'out' | 'overdue' | 'returned'

function stateOf(q: EquipmentItem, now: string): State {
  if (q.returnedOn !== null) return 'returned'
  if (q.dueBackOn !== null && q.dueBackOn < now) return 'overdue'
  return 'out'
}

function money(value: number | null, currency: string): string {
  if (value === null) return 'no value recorded'
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(value)
}

function Item({ q, now, onReturn }: { q: EquipmentItem; now: string; onReturn: () => void }) {
  const state = stateOf(q, now)
  const [open, setOpen] = useState(false)
  const [when, setWhen] = useState(now)
  const [condition, setCondition] = useState<EquipmentCondition>('good')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save() {
    setBusy(true)
    setError(null)
    try {
      await returnEquipment(q.id, when, condition)
      setOpen(false)
      onReturn()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="tl">
      <div className="when">{shortDate(q.issuedOn)}</div>
      <div className="what">
        <div className="headline">
          <strong>{q.item}</strong>
          {q.serialNumber !== null && (
            <span className="mono" style={{ fontSize: 12.5, color: 'var(--ivory-dim)' }}>
              {q.serialNumber}
            </span>
          )}
          {state === 'returned' && <span className="sev info">Returned</span>}
          {state === 'out' && <span className="sev due_soon">Out</span>}
          {state === 'overdue' && <span className="sev overdue">Overdue</span>}
        </div>

        <p>
          {q.category ?? 'Uncategorised'} &middot; {money(q.replacementValue, q.currency)}
          {q.dueBackOn !== null && state !== 'returned' && ` · due back ${shortDate(q.dueBackOn)}`}
          {q.returnedOn !== null &&
            ` · returned ${shortDate(q.returnedOn)}${
              q.conditionOnReturn === null
                ? ''
                : `, ${CONDITIONS.find(([v]) => v === q.conditionOnReturn)?.[1] ?? q.conditionOnReturn}`
            }`}
        </p>

        {q.acknowledgedOn === null && q.replacementValue !== null && state !== 'returned' && (
          <p style={{ color: 'var(--sev-overdue)' }}>
            No signed acknowledgement is on file, so the cost cannot lawfully be deducted from pay.
            Recovering the item is the only route.
          </p>
        )}

        {q.notes !== null && <div className="meta">{q.notes}</div>}

        {state !== 'returned' && !open && (
          <div>
            <button className="btn sm quiet" type="button" onClick={() => setOpen(true)}>
              Record return
            </button>
          </div>
        )}

        {open && (
          <div className="field" style={{ marginTop: 6 }}>
            <label>
              Returned on
              <input type="date" value={when} max={now} onChange={(e) => setWhen(e.target.value)} />
            </label>
            <label>
              Condition
              <select
                value={condition}
                onChange={(e) => setCondition(e.target.value as EquipmentCondition)}
              >
                {CONDITIONS.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
            <button className="btn sm" type="button" onClick={() => void save()} disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button className="btn sm quiet" type="button" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        )}

        {error !== null && (
          <div className="callout" style={{ borderLeftColor: 'var(--sev-critical)' }}>{error}</div>
        )}
      </div>
    </div>
  )
}

export function Equipment({ person }: { person: PersonDetail }) {
  const { current } = useOrg()
  const result = useAsync(
    () => loadEquipment(current.id, person.employment.id),
    [current.id, person.employment.id],
  )

  const [open, setOpen] = useState(false)
  const [item, setItem] = useState('')
  const [category, setCategory] = useState(CATEGORIES[0]!)
  const [serial, setSerial] = useState('')
  const [issuedOn, setIssuedOn] = useState(today())
  const [dueBack, setDueBack] = useState('')
  const [value, setValue] = useState('')
  const [acknowledged, setAcknowledged] = useState(true)
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (result.state === 'loading') return <Loading what="equipment" />
  if (result.state === 'error') return <Failed message={result.message} onRetry={result.reload} />

  const items = result.data
  const now = today()
  const outstanding = items.filter((q) => q.returnedOn === null)
  const outstandingValue = outstanding.reduce((sum, q) => sum + (q.replacementValue ?? 0), 0)
  const valid = item.trim().length > 1

  async function save() {
    setBusy(true)
    setError(null)
    try {
      await issueEquipment({
        organisationId: current.id,
        employmentId: person.employment.id,
        personId: person.person.id,
        item,
        category,
        serialNumber: serial,
        issuedOn,
        dueBackOn: dueBack === '' ? null : dueBack,
        replacementValue: value.trim() === '' ? null : Number(value),
        acknowledgedOn: acknowledged ? issuedOn : null,
        notes,
      })
      setOpen(false)
      setItem('')
      setSerial('')
      setValue('')
      setNotes('')
      result.reload()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <div className="head">
        <h2>Equipment</h2>
        <div className="spacer" />
        {outstanding.length > 0 && (
          <span className="mono" style={{ fontSize: 12.5, color: 'var(--ivory-dim)' }}>
            {outstanding.length} out
            {outstandingValue > 0 && `, ${money(outstandingValue, 'GBP')}`}
          </span>
        )}
        <button className="btn sm" type="button" onClick={() => setOpen((v) => !v)}>
          {open ? 'Cancel' : 'Issue an item'}
        </button>
      </div>

      {open && (
        <div className="addform">
          <h3>Issue an item</h3>
          <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--ivory-dim)', maxWidth: '66ch' }}>
            The acknowledgement matters. Cost cannot lawfully be recovered from final pay without
            the team member's prior written agreement, so leave the box unticked when they have not
            actually signed for it, rather than ticking it to make the record look tidy.
          </p>
          <div className="grid">
            <label>
              Item
              <input
                type="text"
                value={item}
                onChange={(e) => setItem(e.target.value)}
                placeholder="Master key fob"
              />
            </label>
            <label>
              Category
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                {CATEGORIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </label>
            <label>
              Serial or asset tag
              <input
                type="text"
                value={serial}
                onChange={(e) => setSerial(e.target.value)}
                placeholder="Optional"
              />
            </label>
            <label>
              Issued on
              <input type="date" value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} />
            </label>
            <label>
              Due back
              <input
                type="date"
                value={dueBack}
                onChange={(e) => setDueBack(e.target.value)}
              />
            </label>
            <label>
              Replacement value, £
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Optional"
              />
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
              Signed for on the day it was issued
            </label>
            <label className="wide">
              Notes
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
            </label>
            <div>
              <button
                className="btn"
                type="button"
                onClick={() => void save()}
                disabled={busy || !valid}
              >
                {busy ? 'Saving…' : 'Issue item'}
              </button>
            </div>
          </div>
          {error !== null && (
            <div className="callout" style={{ marginTop: 14, borderLeftColor: 'var(--sev-critical)' }}>
              {error}
            </div>
          )}
        </div>
      )}

      <div className="body flush">
        {items.length === 0 ? (
          <div className="empty">
            <p>Nothing issued. Keys, fobs, uniform, radios and lockers all belong here.</p>
          </div>
        ) : (
          <div className="timeline">
            {items.map((q) => (
              <Item key={q.id} q={q} now={now} onReturn={result.reload} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
